/* =============================================================
   PartnersList.jsx — University partners list (admin)
   -------------------------------------------------------------
   Displays all partner universities with search, type filter,
   and pagination (5 per page). Supports adding partners and
   CSV import. Invite button opens a pre-filled mailto link.

   TODO (backend integration):
   - Replace usePartnerList hook data with Supabase query
   ============================================================= */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { usePartnerList } from '../hooks/usePartnerList';
import AddPartnerModal from '../components/AddPartnerModal';
import ImportPartnersModal from '../components/ImportPartnersModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

/* ─── HELPERS ───────────────────────────────────────────── */

/* exportCSV — downloads filtered partner rows as a CSV file   */
function exportCSV(rows) {
  const headers = ['University Name', 'Country', 'Type', 'Students This Semester', 'Max', 'Connection Status'];
  const data = rows.map(p => [p.name, p.country.replace(/[^\x00-\x7F]/g, '').trim(), p.type, p.students, p.max, p.connection]);
  const csv  = [headers, ...data].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'university_partners_unixplore.csv'; a.click();
  URL.revokeObjectURL(url);
}

/* ─── PARTNERS LIST COMPONENT ───────────────────────────── */

export default function PartnersList() {
  const navigate = useNavigate();
  const { partners, addPartner, addPartners, deletePartner, deleteAllPartners } = usePartnerList();

  const [showAdd,        setShowAdd]        = useState(false);
  const [showImport,     setShowImport]     = useState(false);
  const [deleteTarget,   setDeleteTarget]   = useState(null);
  const [showDeleteAll,  setShowDeleteAll]  = useState(false);
  const [search,      setSearch]      = useState('');
  const [connFilter,  setConnFilter]  = useState('');
  const [typeFilter,  setTypeFilter]  = useState('');
  const [page,        setPage]        = useState(1);

  const PER_PAGE = 5;
  const setFilter = (setter) => (val) => { setter(val); setPage(1); };

  const filtered = partners.filter(p => {
    const searchOk = !search     || p.name.toLowerCase().includes(search.toLowerCase()) || p.abbr.toLowerCase().includes(search.toLowerCase()) || p.country.toLowerCase().includes(search.toLowerCase());
    const connOk   = !connFilter || p.connection === connFilter;
    const typeOk   = !typeFilter || p.type === typeFilter;
    return searchOk && connOk && typeOk;
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const hasFilters = search || connFilter || typeFilter;

  return (
    <Layout>
      {showAdd && (
        <AddPartnerModal
          onAdd={(data) => { addPartner(data); setShowAdd(false); }}
          onClose={() => setShowAdd(false)}
        />
      )}
      {showImport && (
        <ImportPartnersModal
          onImport={async (rows) => { await addPartners(rows); setShowImport(false); }}
          onClose={() => setShowImport(false)}
        />
      )}
      {showDeleteAll && (() => {
        const linkedStudents = partners.reduce((sum, p) => sum + (p.students ?? 0), 0);
        return (
          <DeleteConfirmModal
            title={`Delete all ${partners.length} partner universities?`}
            message="Every partner university will be permanently removed from your list. This action cannot be undone."
            warning={
              linkedStudents > 0
                ? `${linkedStudents} student${linkedStudents > 1 ? 's are' : ' is'} linked to these partners. Deleting and re-importing will break those connections. Delete students first if you plan to re-import.`
                : null
            }
            onConfirm={async () => { await deleteAllPartners(); setShowDeleteAll(false); }}
            onClose={() => setShowDeleteAll(false)}
          />
        );
      })()}
      {deleteTarget && (
        <DeleteConfirmModal
          title={`Delete ${deleteTarget.name}?`}
          message="This partner university will be permanently removed from your list. This action cannot be undone."
          warning={
            deleteTarget.students > 0
              ? `${deleteTarget.students} student${deleteTarget.students > 1 ? 's are' : ' is'} linked to this partner. Deleting and re-importing will break that connection. Delete students first if you plan to re-import.`
              : null
          }
          onConfirm={async () => {
            await deletePartner(deleteTarget.id);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">University Partners</h1>
          <p className="text-[#464555] mt-1">
            {filtered.length !== partners.length
              ? `${filtered.length} of ${partners.length} partners shown`
              : `${partners.length} active partner universities across ${new Set(partners.map(p => p.country)).size} countries.`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => exportCSV(filtered)}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">download</span>
            Export CSV
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 border border-indigo-200 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">upload_file</span>
            Import Partners
          </button>
          {partners.length > 0 && (
            <button
              onClick={() => setShowDeleteAll(true)}
              className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-500 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors"
            >
              <span className="material-symbols-outlined text-[16px]">delete_sweep</span>
              Delete All
            </button>
          )}
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#4343d5] text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">add</span>
            Add Partner
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search universities..."
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 text-slate-700 placeholder-slate-400"
          />
        </div>
        <select
          value={typeFilter}
          onChange={e => setFilter(setTypeFilter)(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
        >
          <option value="">All Types</option>
          <option value="ERASMUS">ERASMUS</option>
          <option value="BILATERAL">BILATERAL</option>
          <option value="DOUBLE DEGREE">DOUBLE DEGREE</option>
        </select>
        {hasFilters && (
          <button
            onClick={() => { setSearch(''); setConnFilter(''); setTypeFilter(''); setPage(1); }}
            className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <span className="material-symbols-outlined text-[14px]">close</span>
            Clear
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-6">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-6 py-3.5">University Name</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Country</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Type</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Students This Semester</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">
                <div className="flex items-center gap-1 group relative">
                  Connection Status
                  <span className="material-symbols-outlined text-[14px] text-slate-300 cursor-pointer">info</span>
                  <div className="absolute left-0 top-6 z-50 hidden group-hover:block w-64 bg-[#0b1c30] text-white text-xs rounded-xl shadow-xl p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0 mt-0.5"></span>
                      <div><span className="font-semibold">Connected:</span> both universities on Unixplore, fully automated.</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-slate-400 font-bold leading-none flex-shrink-0 mt-0.5">—</span>
                      <div><span className="font-semibold">—</span> not yet connected to Unixplore. Invite to activate.</div>
                    </div>
                  </div>
                </div>
              </th>
              <th className="px-4 py-3.5" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-400">
                  No partners match the selected filters.
                </td>
              </tr>
            ) : (
              paginated.map((p, i) => (
                <tr
                  key={i}
                  onClick={() => navigate(`/partners/${p.id}`)}
                  className="border-b border-slate-50 last:border-0 hover:bg-[#F0F2FF] transition-colors cursor-pointer group"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-slate-600">{p.abbr}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#0b1c30]">{p.name}</span>
                          {p.isNew && (
                            <span className="text-[10px] font-bold bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">New</span>
                          )}
                        </div>
                        {!p.linked_university_id && (() => {
                          const missing = [
                            !p.key_dates.length     && 'key dates',
                            !p.useful_links.length  && 'useful links',
                            !p.contact_emails.length && 'contacts',
                          ].filter(Boolean);
                          if (!missing.length) return null;
                          return (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full w-fit">
                              <span className="material-symbols-outlined text-[11px]">warning</span>
                              Incomplete — missing {missing.join(', ')}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-[#464555]">{p.country}</td>
                  <td className="px-4 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      p.type === 'ERASMUS'       ? 'bg-blue-100 text-blue-700' :
                      p.type === 'DOUBLE DEGREE' ? 'bg-emerald-100 text-emerald-700' :
                                                   'bg-violet-100 text-violet-700'
                    }`}>{p.type}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-[#0b1c30]">{p.students}</span>
                      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-400 rounded-full" style={{ width: `${(p.students / p.max) * 100}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 w-fit ${p.connStyle}`}>
                      {p.dot && <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`}></span>}
                      {p.connection}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {p.action !== 'View' ? (
                        <a
                          href={`mailto:${p.email}?subject=Unixplore Partnership Invitation — ESMT Berlin&body=Dear International Office team,%0A%0AWe would like to invite ${p.name} to join Unixplore, our exchange management platform.%0A%0ABest regards,%0AESMT Berlin International Office`}
                          onClick={e => e.stopPropagation()}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-800 px-3 py-1.5 rounded-lg border border-indigo-300 hover:bg-indigo-50 transition-colors inline-block"
                        >
                          Invite
                        </a>
                      ) : (
                        <span className="material-symbols-outlined text-slate-300 text-[18px] group-hover:text-indigo-400 transition-colors">arrow_forward</span>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteTarget(p); }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all"
                        title="Delete partner"
                      >
                        <span className="material-symbols-outlined text-[16px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {filtered.length === 0
              ? 'No partners match the selected filters'
              : `Showing ${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filtered.length)} of ${filtered.length} partners`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-[16px]">chevron_left</span>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                  n === page ? 'bg-indigo-600 text-white' : 'border border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
              className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-[16px]">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      {/* Automation tip */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 flex gap-3 mt-6">
        <span className="material-symbols-outlined text-indigo-500 text-[22px] flex-shrink-0 mt-0.5">insights</span>
        <div>
          <p className="text-sm font-semibold text-indigo-800">Automation Tip</p>
          <p className="text-xs text-indigo-600 mt-1 leading-relaxed">
            Inviting <strong>Manual</strong> partners to Unixplore can reduce administrative workload by up to <strong>40%</strong>.
          </p>
        </div>
      </div>
    </Layout>
  );
}
